import { useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Field, RadioPills, Honeypot, SubmitButton, SuccessPanel, inputCls, useEnquiry } from "@/components/forms/fields";
import { uploadPhoto } from "@/lib/api";

const INTERESTED_IN = ["Ducted", "Split System", "Multi-Head", "VEU Upgrade", "Not Sure"];

export default function QuickQuote() {
  const { data, set, errors, status, submit, reset, formProps } = useEnquiry(
    "quote",
    ["full_name", "phone", "suburb_postcode", "interested_in"],
    "/"
  );
  const [photoName, setPhotoName] = useState("");
  const [uploading, setUploading] = useState(false);

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadPhoto(file);
      set("photo", res.url);
      setPhotoName(file.name);
      toast.success("Photo attached.");
    } catch (err) {
      toast.error("Photo upload failed — you can still submit without it.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="bg-mist py-24 md:py-32" data-testid="quick-quote-section">
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <span className="inline-flex items-center gap-3 font-display text-xs font-bold uppercase tracking-[0.25em] text-brand-blue md:text-sm">
            <span className="h-px w-8 bg-gradient-to-r from-navy to-brand-cyan" aria-hidden="true" />
            Free Quote
          </span>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Get a free quote in under a minute.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-mutedblue">
            A few quick details and our team will come back to you with practical options for your
            property — no obligation, no pressure.
          </p>
          <ul className="mt-6 space-y-3 text-sm font-medium text-ink">
            {["6,500+ installations completed since 2000", "Advice tailored to your home and budget", "VEU rebate guidance where applicable"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-[20px] border border-cardline bg-white p-7 shadow-[0_16px_50px_rgba(5,59,117,0.07)] md:p-9">
          {status === "success" ? (
            <SuccessPanel
              testid="quick-quote-success"
              title="Thank you — your quote request is in."
              message="Our team will review your details and contact you shortly to discuss the right system for your home."
              onReset={reset}
            />
          ) : (
            <form onSubmit={submit} {...formProps} noValidate data-testid="quick-quote-form" className="space-y-5">
              <Honeypot value={data.website || ""} onChange={(v) => set("website", v)} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name" required error={errors.full_name}>
                  <input data-testid="quick-name" className={inputCls} value={data.full_name || ""} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" />
                </Field>
                <Field label="Phone" required error={errors.phone}>
                  <input data-testid="quick-phone" type="tel" className={inputCls} value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                </Field>
              </div>
              <Field label="Suburb / Postcode" required error={errors.suburb_postcode}>
                <input data-testid="quick-suburb" className={inputCls} value={data.suburb_postcode || ""} onChange={(e) => set("suburb_postcode", e.target.value)} placeholder="e.g. Narre Warren 3805" />
              </Field>
              <Field label="Interested In" required error={errors.interested_in}>
                <RadioPills testid="quick-interested" options={INTERESTED_IN} value={data.interested_in} onChange={(v) => set("interested_in", v)} />
              </Field>
              <Field label="Message">
                <textarea data-testid="quick-message" rows={3} className={inputCls} value={data.message || ""} onChange={(e) => set("message", e.target.value)} placeholder="Anything we should know about your home?" />
              </Field>
              <div>
                <label
                  data-testid="quick-photo-label"
                  className="flex cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-dashed border-line bg-cloud px-4 py-3.5 text-xs font-semibold text-mutedblue transition-[border-color,color] duration-200 hover:border-brand-cyan hover:text-navy"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 text-brand-blue" />}
                  {photoName ? `Attached: ${photoName}` : "Attach a photo (optional)"}
                  <input data-testid="quick-photo" type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                </label>
              </div>
              <SubmitButton status={status} testid="quick-quote-submit">Get My Free Quote</SubmitButton>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
